import React, { useCallback, useEffect, useState } from 'react';
import { RobotActivityOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect';
import { RuleMetadataProvider } from '../metadata/RuleMetadataProvider';
import { useRuleApi } from '../useRuleApi';
import ActivityCounters from './ActivityCounters';
import ActivityFeed from './ActivityFeed';
import ExecutionLog from './ExecutionLog';
import PlannedTable from './PlannedTable';

interface ComponentProps {
  /** Undefined means the logged-in user's own company; the admin section passes the row it opened. */
  companyId?: string;
}

type View = 'ACTIVITY' | 'EXECUTIONS' | 'PLANNED';

/**
 * What the robot did, is doing and is about to do.
 *
 * Three answers to three questions — *is it working*, *why didn't my rule fire*, *what is next* —
 * sharing one set of counters at the top so the state of the engine is visible from any of them.
 */
const AiDispatcherActivity: React.FC<ComponentProps> = ({ companyId }) => {
  const { tr } = useTranslationLgs();
  const { activity } = useRuleApi(companyId);

  const [view, setView] = useState<View>('ACTIVITY');
  const [counters, setCounters] = useState<RobotActivityOutDTO>();

  const loadCounters = useCallback(() => {
    // A background refresh must not raise a toast when the network hiccups.
    activity(setCounters, { onError: () => undefined });
  }, [activity]);

  useEffect(() => {
    loadCounters();
  }, [loadCounters]);

  return (
    // The vocabulary is fetched here too, not only by the editor: a trigger is shown by its own
    // name rather than its key, and that name comes from `/metadata` like everything else.
    <RuleMetadataProvider>
      <div className="flex flex-col gap-6">
        <ActivityCounters counters={counters} />

        <InputSelect
          model={[
            { code: 'ACTIVITY', label: tr('AiDispatcher.viewActivity', 'Activity') },
            { code: 'EXECUTIONS', label: tr('AiDispatcher.viewExecutions', 'Execution log') },
            { code: 'PLANNED', label: tr('AiDispatcher.viewPlanned', 'Planned') },
          ]}
          selectedItemCode={view}
          onSelect={code => setView(code as View)}
        />

        {view === 'ACTIVITY' && <ActivityFeed companyId={companyId} onTick={loadCounters} />}
        {view === 'EXECUTIONS' && <ExecutionLog companyId={companyId} />}
        {view === 'PLANNED' && <PlannedTable companyId={companyId} />}
      </div>
    </RuleMetadataProvider>
  );
};

export default AiDispatcherActivity;
